import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { User, UserRound, Sparkles, Film, Play, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { unlock, playOn } from "@/components/voiceKit";

const KINDS = [{ v: "real", label: "عادي", Icon: Film }, { v: "animation", label: "أنيميشن", Icon: Sparkles }];
const GENDERS = [{ v: "male", label: "رجل", Icon: User }, { v: "female", label: "امرأة", Icon: UserRound }];
let cache = null;
let sharedAudio = null;

const useVoices = () => {
  const [d, setD] = useState(cache || { voices: [], styles: [] });
  useEffect(() => {
    if (cache) return;
    Promise.all([api.get("/voices"), api.get("/voice-styles")])
      .then(([v, s]) => { cache = { voices: v.data, styles: s.data }; setD(cache); }).catch(() => {});
  }, []);
  return d;
};

const Pills = ({ items, value, onChange, testId, small }) => (
  <div className="inline-flex flex-wrap rounded-full border border-border bg-secondary p-1 gap-1">
    {items.map(({ v, label, Icon }) => (
      <button key={v} type="button" onClick={() => onChange(v)} data-testid={`${testId}-${v || "auto"}`}
        className={`flex items-center gap-1.5 rounded-full px-3 ${small ? "py-1 text-xs" : "py-1.5 text-sm"} transition-colors ${value === v ? "bg-primary text-white" : "text-muted-foreground hover:text-foreground"}`}>
        {Icon && <Icon className="w-3.5 h-3.5" />} {label}
      </button>
    ))}
  </div>
);

const useSample = (previewRequest) => {
  const [state, setState] = useState({});
  const play = async (voiceId, style) => {
    if (!sharedAudio) sharedAudio = new Audio();
    unlock(sharedAudio);
    const key = `${voiceId}:${style}`;
    setState({ key, busy: true });
    if (previewRequest) {  // نص العميل نفسه بالصوت المختار — التسجيل ذاته يُعاد في «جرّب الصوت» وفي الفيديو (لا عيّنة عامة مختلفة)
      try {
        const r = await previewRequest(voiceId, style);
        playOn(sharedAudio, URL.createObjectURL(r.data));
        return setState({ key });
      } catch (e) {
        let d = e.response?.data;
        if (d instanceof Blob) { try { d = JSON.parse(await d.text()); } catch { d = null; } }
        toast.error(d?.detail || "تعذّر توليد الصوت");
        return setState({});
      }
    }
    const end = Date.now() + 150000;
    try {
      while (Date.now() < end) {
        const r = await api.get(`/voices/${voiceId}/sample`, { params: { style }, responseType: "blob", validateStatus: (s) => s === 200 || s === 202 });
        if (r.status === 200) {
          playOn(sharedAudio, URL.createObjectURL(r.data));
          return setState({ key });
        }
        await new Promise((ok) => setTimeout(ok, 4000));
      }
      toast.error("العينة تتجهز، جرّب بعد قليل");
    } catch { toast.error("تعذّر تشغيل العينة"); }
    setState({});
  };
  return { state, play };
};

const ListenBtn = ({ busy, onClick, testid }) => (
  <button type="button" onClick={(e) => { e.stopPropagation(); onClick(); }} data-testid={testid}
    className="inline-flex items-center gap-1 text-[11px] text-primary hover:underline">
    {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Play className="w-3 h-3" />} {busy ? "تجهيز…" : "استمع"}
  </button>
);

const MENS = /ثوب|ثياب|شماغ|غترة|عقال|بشت|رجالي|thobe|shemagh|men'?s/i;
const WOMENS = /عباي|عبايات|شيلة|فستان|نسائي|abaya|women'?s|dress/i;
/** تنبيه قبل التوليد: ملبوس لجنس مختلف عن الشخصية يُعرض على مانيكان بلا أشخاص — ليختار العميل عن وعي بدل أن يتفاجأ */
export const garmentNote = (productName, gender) => {
  if (!productName || !gender || gender === "animation") return "";
  if (MENS.test(productName) && gender === "female") return `«${productName}» ملبوس رجالي والشخصية امرأة: لن تلبسه المرأة أبدًا — سيُعرض على مانيكان بلا أشخاص. اختر «رجل» إن أردت شخصًا يرتديه.`;
  if (WOMENS.test(productName) && gender === "male") return `«${productName}» ملبوس نسائي والشخصية رجل: لن يلبسه الرجل أبدًا — سيُعرض على مانيكان بلا أشخاص. اختر «امرأة» إن أردت شخصًا يرتديه.`;
  return "";
};

const GarmentNote = ({ note, testId }) => note ? (
  <p className="w-full text-[11px] text-amber-800 dark:text-amber-300 bg-amber-500/10 border border-amber-500/30 rounded-md px-2 py-1" data-testid={`${testId}-garment-note`}>{note}</p>
) : null;

/** Client choice: type → gender → character (listen) → delivery style. Voices are the owner-prepared characters only (no automatic pick). */
export const CharacterPicker = ({ value, onChange, pick = {}, onPick, testId = "character", compact = false, productName = "", previewRequest, hint = "بعد الاختيار: زر واحد تحت السكربت «جرّب الصوت» يسمعك التعليق نفسه بالصوت والأسلوب المختارين." }) => {
  const { voices, styles } = useVoices();
  const { state, play } = useSample(previewRequest);
  const kind = value === "animation" ? "animation" : "real";
  const gender = kind === "animation" ? (pick.animGender || "") : (value === "female" ? "female" : value === "male" ? "male" : "");
  const set = (p) => onPick && onPick({ ...pick, ...p });
  const list = voices.filter((v) => v.enabled !== false && (!gender || v.gender === gender));
  const chosen = list.some((v) => v.id === pick.voice) ? pick.voice : (list[0]?.id || "");
  const note = garmentNote(productName, kind === "animation" ? "" : gender);
  // الصوت المعروض هو المُرسل دائمًا: نثبّت أول شخصية من مكتبة الأدمن في الاختيار بدل تركه فارغًا (لا اختيار تلقائي في الخادم)
  useEffect(() => { if (onPick && chosen && pick.voice !== chosen) onPick({ ...pick, voice: chosen }); }, [chosen]); // eslint-disable-line
  const styleItems = [{ v: "", label: "طبيعي" }, ...styles.map((s) => ({ v: s.id, label: s.name }))];
  const pickStyle = (s) => { set({ style: s }); if (chosen) play(chosen, s); };
  if (compact) {
    const Row = ({ n, label, children }) => (
      <div className="flex items-center gap-2 flex-wrap">
        <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-primary/15 text-primary text-[11px] font-bold">{n}</span>
        <span className="text-xs font-bold text-foreground w-24">{label}</span>
        {children}
      </div>
    );
    return (
      <div className="w-full rounded-lg border border-border bg-secondary/40 p-2.5 space-y-2" data-testid={`${testId}-picker`}>
        <Row n="١" label="الشخصية"><Pills small testId={testId} value={value} onChange={onChange} items={[...GENDERS, { v: "animation", label: "أنيميشن", Icon: Sparkles }]} /></Row>
        {onPick && list.length > 0 && (
          <Row n="٢" label="الصوت">
            <select value={chosen} onChange={(e) => set({ voice: e.target.value })} data-testid={`${testId}-voice-select`}
              className="h-7 rounded-full border border-border bg-secondary px-3 text-xs">
              {list.map((v) => <option key={v.id} value={v.id}>{v.name}</option>)}
            </select>
          </Row>
        )}
        {onPick && styles.length > 0 && (
          <Row n="٣" label="أسلوب الأداء"><Pills small testId={`${testId}-style`} value={pick.style || ""} onChange={(s) => set({ style: s })} items={styleItems} /></Row>
        )}
        {hint && <p className="text-[11px] text-muted-foreground">{hint}</p>}
        <GarmentNote note={note} testId={testId} />
      </div>
    );
  }
  const setKind = (k) => { onChange(k === "animation" ? "animation" : (pick.animGender || "male")); };
  const setGender = (g) => { if (kind === "animation") set({ animGender: g, voice: "" }); else { onChange(g); set({ voice: "" }); } };
  return (
    <div className="space-y-3" data-testid={`${testId}-picker`}>
      <div className="flex flex-wrap items-center gap-3">
        <span className="text-sm font-medium text-muted-foreground w-24">نوع الفيديو</span>
        <Pills testId={`${testId}-kind`} value={kind} onChange={setKind} items={KINDS} />
      </div>
      <div className="flex flex-wrap items-center gap-3">
        <span className="text-sm font-medium text-muted-foreground w-24">الشخصية</span>
        <Pills testId={testId} value={gender} onChange={setGender} items={GENDERS} />
      </div>
      <div className="space-y-1.5">
        <span className="text-sm font-medium text-muted-foreground">الصوت <span className="text-xs">{previewRequest ? "(استمع لنصّك بكل شخصية — ما تسمعه هنا هو حرفيًا ما يُنطق في الفيديو)" : "(شخصيات رواج الصوتية — استمع واختر)"}</span></span>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
          {list.map((v) => (
            <div key={v.id} role="button" tabIndex={0} onClick={() => set({ voice: v.id })} data-testid={`${testId}-voice-${v.id}`}
              className={`rounded-xl border p-3 text-start cursor-pointer transition-colors ${chosen === v.id ? "border-primary bg-primary/10" : "border-border hover:border-primary/50"}`}>
              <div className="font-bold text-sm">{v.name}</div>
              <div className="flex items-center justify-between mt-1">
                <span className="text-[11px] text-muted-foreground">{v.gender === "female" ? "امرأة" : "رجل"}</span>
                <ListenBtn busy={state.busy && state.key === `${v.id}:${pick.style || ""}`} onClick={() => play(v.id, pick.style || "")} testid={`${testId}-listen-${v.id}`} />
              </div>
            </div>
          ))}
        </div>
      </div>
      <div className="flex flex-wrap items-center gap-3">
        <span className="text-sm font-medium text-muted-foreground w-24">أسلوب الأداء</span>
        <Pills small testId={`${testId}-style`} value={pick.style || ""} onChange={pickStyle} items={styleItems} />
      </div>
      <GarmentNote note={note} testId={testId} />
      <p className="text-xs text-muted-foreground">{chosen ? "اضغط أسلوبًا (رسمي/حماسي/فخم/إذاعي) لتسمعه بصوت الشخصية فورًا — «طبيعي» هو الصوت المدرّب بلا تأثير." : "اختر شخصية واضغط استمع لتجربة صوتها قبل التوليد."}</p>
    </div>
  );
};
